import type { CSSProperties } from "react";
import Reveal from "@/components/Reveal";
import { Star, Shield, Paintbrush, CheckCircle, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { heroPhotoSet } from "@/lib/media";
import FormEmbed from "@/components/FormEmbed";

const badges = [
  { icon: Shield, label: "Licensed & Insured" },
  { icon: Star, label: "5-Star Reviews" },
  { icon: Clock, label: "Open 24/7" },
  { icon: CheckCircle, label: "Free Estimates" },
];

const stats = [
  { value: "24", label: "Always Open", suffix: "/7" },
  { value: "15", label: "Cities Served", suffix: "+" },
  { value: "10", label: "Military Discount", suffix: "%" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <img
        src={heroPhotoSet.src}
        srcSet={heroPhotoSet.srcSet}
        sizes="100vw"
        alt="Freshly painted home exterior by 4 Seasons Painting in Phoenix, Arizona"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        decoding="async"
        width={heroPhotoSet.width}
        height={heroPhotoSet.height}
        fetchPriority="high"
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(235,62%,8%/0.78)] via-[hsl(235,62%,8%/0.65)] to-[hsl(2,60%,18%/0.45)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      <div
        className="hero-blob absolute top-20 right-20 w-96 h-96 rounded-full hidden md:block"
        style={
          {
            background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)",
            "--blob-duration": "6s",
            "--blob-scale": "1.1",
            "--blob-opacity-low": "0.18",
            "--blob-opacity-high": "0.3",
          } as CSSProperties
        }
      />
      <div
        className="hero-blob absolute bottom-40 left-10 w-64 h-64 rounded-full hidden md:block"
        style={
          {
            background: "radial-gradient(circle, hsl(var(--secondary) / 0.4), transparent 70%)",
            "--blob-duration": "8s",
            "--blob-delay": "2s",
            "--blob-scale": "1.15",
            "--blob-opacity-low": "0.12",
            "--blob-opacity-high": "0.25",
          } as CSSProperties
        }
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Reveal
              as="div"
              y={30}
              duration={0.6}
              immediate
            >
              <Reveal
                as="div"
                x={-20}
                delay={0.15}
                duration={0.5}
                immediate
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <Paintbrush className="h-4 w-4 text-tertiary" />
                <span className="text-white">Phoenix & the Entire Valley · Open 24/7</span>
              </Reveal>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                Color That Lasts
                <br />
                <span className="text-gradient-brand">All Four</span> Seasons.
              </h1>

              {/* Readable tagline with glass background card */}
              <div className="inline-block bg-[#080b1c]/55 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-8 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  <span className="font-bold text-white">4 Seasons Painting</span> handles interior
                  and exterior painting, power washing, sun screens, rain gutters, and custom
                  pergolas across Phoenix and the whole Valley — 24 hours a day, 7 days a week.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button asChild
                  variant="outline"
                  size="lg"
                  className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold"
                >
                  <a href={siteConfig.phoneHref}><Phone className="h-5 w-5" /> {siteConfig.phone}</a>
                </Button>
                <Button asChild
                  variant="outline"
                  size="lg"
                  className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold"
                >
                  <a href={siteConfig.secondaryPhoneHref}><Phone className="h-5 w-5" /> {siteConfig.secondaryPhone}</a>
                </Button>
              </div>

              <Reveal
                as="div"
                y={10}
                delay={0.6}
                duration={0.5}
                immediate
                className="flex flex-wrap gap-3"
              >
                <span className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3">
                  <span className="text-2xl">🎁</span>
                  <span className="text-white font-semibold text-sm">
                    <span className="text-tertiary font-bold">$100 Gift Card</span> for Every Referral
                  </span>
                </span>
                <span className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3">
                  {/* Medal, not a flag: Windows has no glyph for regional-indicator
                      flag emoji and renders them as bare "US" letters. */}
                  <span className="text-2xl">🎖️</span>
                  <span className="text-white font-semibold text-sm">
                    <span className="text-tertiary font-bold">10% Off</span> Military & Veterans
                  </span>
                </span>
              </Reveal>
            </Reveal>
          </div>

          {/* Form widget — exactly as currently positioned, right-side hero, original dimensions preserved */}
          <Reveal
            as="div"
            x={40}
            delay={0.3}
            duration={0.7}
            immediate
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-white">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  No pressure, no obligation — just honest, written pricing.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" deferUntilIdle />
            </div>
          </Reveal>
        </div>

        <Reveal
          as="div"
          y={20}
          delay={0.7}
          duration={0.5}
          immediate
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-5">
            {stats.map((stat, i) => (
              <Reveal
                as="div"
                y={20}
                delay={0.8 + i * 0.15}
                duration={0.5}
                immediate
                key={stat.label}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-tertiary">{stat.suffix}</span>
                </div>
                <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">{stat.label}</div>
              </Reveal>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-tertiary/25 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-tertiary" />
                </div>
                <span className="text-white/85 text-xs sm:text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Concave/curved bottom edge */}
      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
